import React, { useState, useEffect } from 'react';
import { Row, Col, Pagination, Spin, Empty, Card, Space, Tag, Typography } from 'antd';
import { 
  FireOutlined, 
  ClockCircleOutlined,
  EyeOutlined 
} from '@ant-design/icons';
import PostCard from '../../components/PostCard';
import SidebarArchive from '../../components/Archive';
import { api } from '../../api/mockData';
import './index.css';

const { Title, Text } = Typography;

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 6,
    total: 0
  });

  const [featuredPost, setFeaturedPost] = useState(null);
  const [hotPosts, setHotPosts] = useState([]);

  useEffect(() => {
    fetchPosts(1);
    // 设置推荐文章（阅读量最高的）
    setFeaturedPost({
      id: 1,
      title: 'React Hooks 完全指南',
      summary: '深入学习React Hooks的使用方法和最佳实践，包括useState、useEffect、useContext等核心Hook的详细解析。',
      coverImage: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&h=400&fit=crop',
      viewCount: 1250,
      createdAt: '2023-10-15T10:30:00Z'
    });
    
    // 设置热门文章
    setHotPosts([
      { id: 1, title: 'React Hooks 完全指南', viewCount: 1250 },
      { id: 2, title: 'Node.js 性能优化实战', viewCount: 980 },
      { id: 3, title: '使用Docker部署微服务', viewCount: 1560 },
      { id: 4, title: 'TypeScript 类型体操入门', viewCount: 890 },
      { id: 5, title: '机器学习基础：线性回归', viewCount: 1120 },
    ]);
  }, []);

  const fetchPosts = async (page = 1) => {
    setLoading(true);
    try {
      const result = await api.getPosts(page, pagination.pageSize);
      setPosts(result.data);
      setPagination({
        ...pagination,
        current: page,
        total: result.total
      });
    } catch (error) {
      console.error('获取文章失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchPosts(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && posts.length === 0) {
    return (
      <div className="spin-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* 顶部横幅 */}
      <div className="hero-banner">
        <div className="banner-content">
          <h1 className="site-title">我的技术博客</h1>
          <p className="site-subtitle">分享技术、记录生活、探索世界</p>
        </div>
      </div>

      {/* 推荐文章 */}
      {featuredPost && (
        <Card className="featured-post" onClick={() => window.location.href = `/post/${featuredPost.id}`}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <div className="featured-image">
                <img src={featuredPost.coverImage} alt={featuredPost.title} />
                <Tag color="red" icon={<FireOutlined />} className="featured-tag">
                  热门推荐
                </Tag>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="featured-content">
                <Title level={3} className="featured-title">
                  {featuredPost.title}
                </Title>
                <Text type="secondary" className="featured-summary">
                  {featuredPost.summary}
                </Text>
                <div className="featured-meta">
                  <Space size="middle">
                    <Space size="small">
                      <EyeOutlined />
                      <Text>{featuredPost.viewCount} 阅读</Text>
                    </Space>
                    <Space size="small">
                      <ClockCircleOutlined />
                      <Text>{new Date(featuredPost.createdAt).toLocaleDateString()}</Text>
                    </Space>
                  </Space>
                  <Tag color="blue" className="read-more">
                    阅读全文 →
                  </Tag>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={[32, 32]}>
        {/* 文章列表 */}
        <Col xs={24} lg={16}>
          <div className="posts-section">
            <Title level={3} className="section-title">📝 最新文章</Title>
            
            {posts.length > 0 ? (
              <>
                <div className="posts-grid">
                  {posts.map(post => (
                    <Col xs={24} sm={12} key={post.id}>
                      <PostCard post={post} />
                    </Col>
                  ))}
                </div>
                
                <div className="pagination-container">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showQuickJumper
                    showTotal={(total) => `共 ${total} 篇文章`}
                  />
                </div>
              </>
            ) : (
              <Empty description="暂无文章" />
            )}
          </div>
        </Col>

        {/* 侧边栏 */}
        <Col xs={24} lg={8}>
          <div className="sidebar-section">
            <SidebarArchive />
            
            {/* 热门文章 */}
            <Card className="sidebar-card">
              <Title level={5} className="sidebar-title">🔥 热门文章</Title>
              <div className="hot-posts">
                {hotPosts.map((post, index) => (
                  <div key={post.id} className="hot-post-item" onClick={() => window.location.href = `/post/${post.id}`}>
                    <span className={`hot-rank ${index < 3 ? 'top-rank' : ''}`}>
                      {index + 1}
                    </span>
                    <div className="hot-post-content">
                      <Text ellipsis className="hot-post-title">
                        {post.title}
                      </Text>
                      <Text type="secondary" className="hot-post-views">
                        {post.viewCount} 阅读
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* 标签云 */}
            <Card className="sidebar-card">
              <Title level={5} className="sidebar-title">🏷️ 标签云</Title>
              <div className="tag-cloud">
                {['React', 'Vue', 'JavaScript', 'Node.js', 'Python', 'CSS', 
                  'HTML', 'TypeScript', 'Docker', 'MySQL', 'MongoDB', 'Redis',
                  '算法', '数据结构', '网络', '安全', '性能优化'].map(tag => (
                  <Tag key={tag} className="cloud-tag">
                    {tag}
                  </Tag>
                ))}
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Home;